import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RotateCcw, Play, XCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { PageHeader, Card, Button, Badge, EmptyState, Spinner } from '../components/ui';
import { restoresApi, backupsApi, platformConnectionsApi } from '../lib/api';
import type { RestoreJob, Snapshot, PlatformConnection } from '../types/vault';

type BadgeTone = 'success' | 'danger' | 'warning' | 'accent' | 'neutral';
const statusTones: Record<string, BadgeTone> = {
  completed: 'success',
  running: 'accent',
  failed: 'danger',
  cancelled: 'neutral',
  pending: 'warning',
};

export default function Restores() {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState<RestoreJob[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(searchParams.get('snapshotId') || '');
  const [selectedConnection, setSelectedConnection] = useState('');
  const [dryRun, setDryRun] = useState(true);
  const [starting, setStarting] = useState(false);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    if (searchParams.get('snapshotId')) {
      setShowStartModal(true);
    }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsRes, snapRes, connRes] = await Promise.all([
        restoresApi.list(),
        backupsApi.listSnapshots(),
        platformConnectionsApi.list(),
      ]);
      if (jobsRes.success && jobsRes.data) setJobs(jobsRes.data.jobs);
      if (snapRes.success && snapRes.data) setSnapshots(snapRes.data.snapshots);
      if (connRes.success && connRes.data) setConnections(connRes.data.connections.filter((c) => c.status === 'active'));
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!selectedSnapshot || !selectedConnection) return;
    setStarting(true);
    try {
      const res = await restoresApi.start({
        snapshotId: selectedSnapshot,
        connectionId: selectedConnection,
        dryRun,
      });
      if (res.success) {
        setShowStartModal(false);
        setSelectedSnapshot('');
        setSelectedConnection('');
        setDryRun(true);
        loadData();
      } else {
        setError(res.message || 'Failed to start restore');
      }
    } catch {
      setError('Failed to start restore');
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await restoresApi.cancel(id);
      loadData();
    } catch {
      setError('Failed to cancel restore');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Vault"
          title="Restores"
          description="Restore data from snapshots to your platforms"
          actions={
            <Button icon={Play} onClick={() => setShowStartModal(true)} disabled={snapshots.length === 0}>
              Start Restore
            </Button>
          }
        />

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-danger font-bold">&times;</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={RotateCcw}
            title="No restore jobs yet"
            description="Start a restore from a backup snapshot."
          />
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} padded>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-semibold text-ink">{job.connectionName || 'Unknown'}</h3>
                      <Badge tone={statusTones[job.status] || 'neutral'} dot>
                        {job.status}
                      </Badge>
                      {job.dryRun && <Badge tone="accent">Dry Run</Badge>}
                    </div>
                    <p className="text-sm text-ink-3 mt-1">
                      {job.resourceTypes.join(', ')} &middot; <span className="font-mono">{job.processedItems}/{job.totalItems}</span> items
                      {job.failedItems > 0 && <span className="text-danger"> ({job.failedItems} failed)</span>}
                    </p>
                    <p className="text-xs text-ink-3 mt-1 font-mono">
                      Started: {job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}
                      {job.completedAt && ` | Completed: ${new Date(job.completedAt).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {(job.status === 'running' || job.status === 'pending') && (
                      <button
                        onClick={() => handleCancel(job.id)}
                        className="text-danger hover:opacity-80"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>

                {job.results && job.results.length > 0 && (
                  <div className="mt-3">
                    <button
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                      className="text-sm text-accent-ink hover:opacity-80"
                    >
                      {expandedJob === job.id ? 'Hide Results' : `View Results (${job.results.length})`}
                    </button>
                    {expandedJob === job.id && (
                      <pre className="mt-2 bg-[var(--code-bg)] text-[var(--code-ink)] border border-line rounded-[11px] p-3 font-mono text-xs max-h-64 overflow-auto">
                        {JSON.stringify(job.results.slice(-50), null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Start Restore Modal */}
        {showStartModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">Start Restore</h2>

              <div className="space-y-4">
                <div>
                  <label className="ds-label">Snapshot</label>
                  <select
                    value={selectedSnapshot}
                    onChange={(e) => setSelectedSnapshot(e.target.value)}
                    className="ds-input"
                  >
                    <option value="">Select a snapshot...</option>
                    {snapshots.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.connectionName || s.platform} - {s.totalItems} items ({new Date(s.createdAt).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ds-label">Target Connection</label>
                  <select
                    value={selectedConnection}
                    onChange={(e) => setSelectedConnection(e.target.value)}
                    className="ds-input"
                  >
                    <option value="">Select a connection...</option>
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>{c.displayName} ({c.platform})</option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    className="rounded border-line text-accent focus:ring-accent-soft"
                  />
                  <span className="text-sm text-ink-2">Dry run (preview changes without applying)</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowStartModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleStart}
                  disabled={starting || !selectedSnapshot || !selectedConnection}
                >
                  {starting ? 'Starting...' : dryRun ? 'Start Dry Run' : 'Start Restore'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
